import React, {
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaBroadcastTower,
  FaCheckCircle,
  FaCopy,
  FaExclamationTriangle,
  FaGripLines,
  FaLayerGroup,
  FaPalette,
  FaPause,
  FaPlay,
  FaPlus,
  FaPowerOff,
  FaSearch,
  FaStepBackward,
  FaStepForward,
  FaTrashAlt,
  FaWindowClose,
} from "react-icons/fa";
import ConfirmModal from "../../components/ConfirmModal";
import TvPresentationPreviewCanvas from "../../components/tv-disply/TvPresentationPreviewCanvas";
import {
  DEFAULT_IMAGE_DURATION_SECONDS,
  DEFAULT_OVERLAY_BACKGROUND_OPACITY,
  MAX_IMAGE_DURATION_SECONDS,
  MAX_TRANSITION_DURATION_MS,
  MIN_IMAGE_DURATION_SECONDS,
  MIN_TRANSITION_DURATION_MS,
  OFFER_TEXT_LIMITS,
  PLAYLIST_FILTERS,
  PLAYLIST_SORT,
  TV_DISPLAY_FIT_MODES,
  TV_DISPLAY_MODES,
  TV_DISPLAY_ORIENTATIONS,
  TV_DISPLAY_TRANSITIONS,
  TV_MEDIA_FILTERS,
  TV_MEDIA_TYPES,
  TV_TEXT_ALIGNMENTS,
  TV_TEXT_ANIMATIONS,
  TV_TEXT_POSITIONS,
} from "../../services/tv-disply/tvDisplayConstants";
import {
  addTvPlaylistItems,
  createTvPlaylist,
  deleteTvPlaylist,
  getDefaultPlaylistItemOverlay,
  normalizePlaylistItemOverlay,
  normalizePlaylistPresentation,
  removeTvPlaylistItem,
  reorderTvPlaylistItems,
  subscribeToTvPlaylistItems,
  updatePlaylistItemOverlay,
  updatePlaylistPresentation,
  updateTvPlaylist,
  updateTvPlaylistItem,
} from "../../services/tv-disply/tvPlaylistService";
import { formatFileSize } from "../../services/tv-disply/tvMediaService";
import "./TvPlaylists.css";

const PLAYLIST_FILTER_OPTIONS = [
  { value: PLAYLIST_FILTERS.ALL, label: "All" },
  { value: PLAYLIST_FILTERS.ACTIVE, label: "Enabled" },
  { value: PLAYLIST_FILTERS.DISABLED, label: "Disabled" },
];

const PLAYLIST_SORT_OPTIONS = [
  { value: PLAYLIST_SORT.NEWEST, label: "Newest" },
  { value: PLAYLIST_SORT.OLDEST, label: "Oldest" },
  { value: PLAYLIST_SORT.NAME_ASC, label: "Name A-Z" },
  { value: PLAYLIST_SORT.NAME_DESC, label: "Name Z-A" },
];

const DISPLAY_MODE_OPTIONS = [
  { value: TV_DISPLAY_MODES.VIDEO_ONLY, label: "Video Only" },
  { value: TV_DISPLAY_MODES.IMAGE_SLIDER, label: "Image Slider" },
  { value: TV_DISPLAY_MODES.IMAGE_WITH_TEXT, label: "Image + Text" },
  { value: TV_DISPLAY_MODES.VIDEO_WITH_TEXT, label: "Video + Text" },
  { value: TV_DISPLAY_MODES.MIXED_PLAYLIST, label: "Mixed Playlist" },
];

const ORIENTATION_OPTIONS = [
  { value: TV_DISPLAY_ORIENTATIONS.LANDSCAPE, label: "Landscape 16:9" },
  { value: TV_DISPLAY_ORIENTATIONS.PORTRAIT, label: "Portrait 9:16" },
];

const FIT_MODE_OPTIONS = [
  { value: TV_DISPLAY_FIT_MODES.CROP, label: "Crop" },
  { value: TV_DISPLAY_FIT_MODES.FIT, label: "Fit" },
  { value: TV_DISPLAY_FIT_MODES.STRETCH, label: "Stretch" },
];

const TRANSITION_OPTIONS = [
  { value: TV_DISPLAY_TRANSITIONS.FADE, label: "Fade" },
  { value: TV_DISPLAY_TRANSITIONS.SLIDE, label: "Slide" },
  { value: TV_DISPLAY_TRANSITIONS.ZOOM, label: "Zoom" },
  { value: TV_DISPLAY_TRANSITIONS.KEN_BURNS, label: "Ken Burns" },
  { value: TV_DISPLAY_TRANSITIONS.SCALE_FADE, label: "Scale + Fade" },
  { value: TV_DISPLAY_TRANSITIONS.SOFT_FLASH, label: "Soft Flash" },
  { value: TV_DISPLAY_TRANSITIONS.RANDOM_PREMIUM, label: "Premium Random" },
];

const POSITION_OPTIONS = [
  TV_TEXT_POSITIONS.TOP_LEFT,
  TV_TEXT_POSITIONS.TOP_CENTER,
  TV_TEXT_POSITIONS.TOP_RIGHT,
  TV_TEXT_POSITIONS.CENTER_LEFT,
  TV_TEXT_POSITIONS.CENTER,
  TV_TEXT_POSITIONS.CENTER_RIGHT,
  TV_TEXT_POSITIONS.BOTTOM_LEFT,
  TV_TEXT_POSITIONS.BOTTOM_CENTER,
  TV_TEXT_POSITIONS.BOTTOM_RIGHT,
];

const TEXT_ALIGNMENT_OPTIONS = [
  { value: TV_TEXT_ALIGNMENTS.LEFT, label: "Left" },
  { value: TV_TEXT_ALIGNMENTS.CENTER, label: "Center" },
  { value: TV_TEXT_ALIGNMENTS.RIGHT, label: "Right" },
];

const TEXT_ANIMATION_OPTIONS = [
  { value: TV_TEXT_ANIMATIONS.NONE, label: "None" },
  { value: TV_TEXT_ANIMATIONS.FADE_IN, label: "Fade In" },
  { value: TV_TEXT_ANIMATIONS.FADE_UP, label: "Fade Up" },
  { value: TV_TEXT_ANIMATIONS.SLIDE_LEFT, label: "Slide Left" },
  { value: TV_TEXT_ANIMATIONS.SLIDE_RIGHT, label: "Slide Right" },
  { value: TV_TEXT_ANIMATIONS.ZOOM_IN, label: "Zoom In" },
];

const EDITOR_TABS = {
  MEDIA: "MEDIA",
  PRESENTATION: "PRESENTATION",
  PREVIEW: "PREVIEW",
};

export default function TvPlaylists({
  playlistRows = [],
  playlistLoading = false,
  playlistError = "",
  mediaRows = [],
  mediaLoading = false,
  mediaError = "",
  playlistStats = { total: 0, enabled: 0, disabled: 0, itemCount: 0 },
}) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filter, setFilter] = useState(PLAYLIST_FILTERS.ALL);
  const [sort, setSort] = useState(PLAYLIST_SORT.NEWEST);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [playlistItems, setPlaylistItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState("");
  const [draggingItemId, setDraggingItemId] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(EDITOR_TABS.MEDIA);
  const [createState, setCreateState] = useState({
    name: "",
    description: "",
    error: "",
    busy: false,
  });
  const [playlistMetaState, setPlaylistMetaState] = useState({
    name: "",
    description: "",
    enabled: true,
    error: "",
    busy: false,
  });
  const [presentationState, setPresentationState] = useState(getPresentationState());
  const [presentationDirty, setPresentationDirty] = useState(false);
  const [presentationBusy, setPresentationBusy] = useState(false);
  const [presentationError, setPresentationError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteState, setDeleteState] = useState({ open: false, busy: false, error: "" });
  const [removeState, setRemoveState] = useState({ open: false, item: null, busy: false });
  const [overlayState, setOverlayState] = useState({ open: false, item: null, value: null, error: "", busy: false });
  const [ordering, setOrdering] = useState(false);
  const [addingMedia, setAddingMedia] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = window.setTimeout(() => setFeedback(null), 3200);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const visiblePlaylists = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    let rows = [...playlistRows];
    if (query) rows = rows.filter((row) => row.searchText.includes(query));
    if (filter === PLAYLIST_FILTERS.ACTIVE) rows = rows.filter((row) => row.enabled);
    if (filter === PLAYLIST_FILTERS.DISABLED) rows = rows.filter((row) => !row.enabled);
    rows.sort((left, right) => sortPlaylists(left, right, sort));
    return rows;
  }, [deferredSearch, filter, playlistRows, sort]);

  const selectedPlaylist = useMemo(
    () => playlistRows.find((row) => row.id === selectedPlaylistId) || null,
    [playlistRows, selectedPlaylistId]
  );

  const mediaMap = useMemo(
    () => new Map(mediaRows.map((row) => [row.id, row])),
    [mediaRows]
  );

  const decoratedItems = useMemo(
    () =>
      playlistItems.map((item) => ({
        ...item,
        media: mediaMap.get(item.mediaId) || null,
      })),
    [mediaMap, playlistItems]
  );

  const compatibility = useMemo(
    () => getCompatibilitySummary(selectedPlaylist?.presentation?.displayMode, decoratedItems),
    [decoratedItems, selectedPlaylist?.presentation?.displayMode]
  );

  const compatibleItems = useMemo(
    () => decoratedItems.filter((item) => isMediaCompatible(item.media, selectedPlaylist?.presentation?.displayMode)),
    [decoratedItems, selectedPlaylist?.presentation?.displayMode]
  );

  const currentPreviewItem = compatibleItems[previewIndex] || compatibility.items[0] || null;

  useEffect(() => {
    if (selectedPlaylistId && playlistRows.some((row) => row.id === selectedPlaylistId)) return;
    startTransition(() => setSelectedPlaylistId(playlistRows[0]?.id || ""));
  }, [playlistRows, selectedPlaylistId]);

  useEffect(() => {
    if (!selectedPlaylistId) {
      setPlaylistItems([]);
      setItemsLoading(false);
      setItemsError("");
      return undefined;
    }

    setItemsLoading(true);
    const unsubscribe = subscribeToTvPlaylistItems(
      selectedPlaylistId,
      (rows) => {
        setPlaylistItems(rows);
        setItemsLoading(false);
        setItemsError("");
      },
      (error) => {
        setItemsError(resolvePlaylistError(error, "Unable to load playlist items in realtime."));
        setItemsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [selectedPlaylistId]);

  useEffect(() => {
    if (!selectedPlaylist) {
      setPlaylistMetaState({ name: "", description: "", enabled: true, error: "", busy: false });
      setPresentationState(getPresentationState());
      setPresentationDirty(false);
      return;
    }

    setPlaylistMetaState({
      name: selectedPlaylist.name,
      description: selectedPlaylist.description,
      enabled: selectedPlaylist.enabled,
      error: "",
      busy: false,
    });
    setPresentationState({
      ...selectedPlaylist.presentation,
      textDefaults: { ...selectedPlaylist.presentation.textDefaults },
    });
    setPresentationDirty(false);
    setPresentationError("");
    setPreviewIndex(0);
    setPreviewPlaying(false);
  }, [selectedPlaylist]);

  useEffect(() => {
    if (!previewPlaying || !currentPreviewItem) return undefined;
    if (currentPreviewItem.media?.type !== TV_MEDIA_TYPES.IMAGE) return undefined;

    const durationSeconds =
      currentPreviewItem.imageDurationSeconds ||
      selectedPlaylist?.presentation?.defaultImageDurationSeconds ||
      DEFAULT_IMAGE_DURATION_SECONDS;

    const timer = window.setTimeout(() => {
      setPreviewIndex((current) => nextPreviewIndex(current, compatibleItems.length));
    }, Math.max(1, durationSeconds) * 1000);

    return () => window.clearTimeout(timer);
  }, [compatibleItems.length, currentPreviewItem, previewPlaying, selectedPlaylist?.presentation?.defaultImageDurationSeconds]);

  function attemptSelectPlaylist(nextId) {
    if (nextId === selectedPlaylistId) return;
    if (presentationDirty) {
      const confirmed = window.confirm("You have unsaved presentation changes. Discard them and switch playlists?");
      if (!confirmed) return;
    }
    startTransition(() => setSelectedPlaylistId(nextId));
  }

  async function handleCreatePlaylist() {
    try {
      setCreateState((current) => ({ ...current, busy: true, error: "" }));
      const playlistId = await createTvPlaylist({
        name: createState.name,
        description: createState.description,
      });
      setCreateState({ name: "", description: "", error: "", busy: false });
      setCreateOpen(false);
      startTransition(() => setSelectedPlaylistId(playlistId));
      setFeedback({ type: "success", message: "Playlist created." });
    } catch (error) {
      setCreateState((current) => ({
        ...current,
        busy: false,
        error: error?.message || "Unable to create playlist.",
      }));
    }
  }

  async function handleSavePlaylistMeta() {
    if (!selectedPlaylist) return;
    try {
      setPlaylistMetaState((current) => ({ ...current, busy: true, error: "" }));
      await updateTvPlaylist(selectedPlaylist.id, {
        name: playlistMetaState.name,
        description: playlistMetaState.description,
        enabled: playlistMetaState.enabled,
      });
      setPlaylistMetaState((current) => ({ ...current, busy: false }));
      setFeedback({ type: "success", message: "Playlist details updated." });
    } catch (error) {
      setPlaylistMetaState((current) => ({
        ...current,
        busy: false,
        error: error?.message || "Unable to update playlist.",
      }));
    }
  }

  async function handleSavePresentation() {
    if (!selectedPlaylist) return;
    try {
      setPresentationBusy(true);
      setPresentationError("");
      const normalized = normalizePlaylistPresentation(presentationState);
      await updatePlaylistPresentation(selectedPlaylist.id, normalized);
      setPresentationState(normalized);
      setPresentationDirty(false);
      setFeedback({ type: "success", message: "Presentation saved." });
    } catch (error) {
      setPresentationError(error?.message || "Unable to save presentation.");
    } finally {
      setPresentationBusy(false);
    }
  }

  async function handleDeletePlaylist() {
    if (!selectedPlaylist) return;
    try {
      setDeleteState((current) => ({ ...current, busy: true, error: "" }));
      await deleteTvPlaylist(selectedPlaylist.id);
      setDeleteState({ open: false, busy: false, error: "" });
      setFeedback({ type: "success", message: "Playlist deleted." });
    } catch (error) {
      setDeleteState({ open: true, busy: false, error: error?.message || "Unable to delete playlist." });
      setFeedback({ type: "error", message: error?.message || "Unable to delete playlist." });
    }
  }

  async function handleAddMedia(selectedIds) {
    if (!selectedPlaylist) return;
    const selectedMedia = mediaRows.filter(
      (row) =>
        selectedIds.includes(row.id) &&
        row.enabled &&
        isMediaCompatible(row, selectedPlaylist.presentation.displayMode)
    );
    if (!selectedMedia.length) {
      setFeedback({ type: "error", message: "Select at least one compatible enabled media item." });
      return;
    }

    try {
      setAddingMedia(true);
      await addTvPlaylistItems(selectedPlaylist.id, selectedMedia);
      setPickerOpen(false);
      setFeedback({
        type: "success",
        message: `${selectedMedia.length} media item${selectedMedia.length === 1 ? "" : "s"} added.`,
      });
    } catch (error) {
      setFeedback({ type: "error", message: error?.message || "Unable to add media to playlist." });
    } finally {
      setAddingMedia(false);
    }
  }

  async function handleMoveItem(fromIndex, toIndex) {
    if (!selectedPlaylist) return;
    if (toIndex < 0 || toIndex >= decoratedItems.length) return;
    try {
      setOrdering(true);
      const nextItems = movePlaylistItems(decoratedItems, fromIndex, toIndex);
      await reorderTvPlaylistItems(selectedPlaylist.id, nextItems);
      setFeedback({ type: "success", message: "Order saved." });
    } catch (error) {
      setFeedback({ type: "error", message: error?.message || "Unable to reorder playlist items." });
    } finally {
      setOrdering(false);
    }
  }

  async function handleToggleItem(item) {
    if (!selectedPlaylist) return;
    try {
      await updateTvPlaylistItem(selectedPlaylist.id, item.id, {
        enabled: !item.enabled,
      });
      setFeedback({ type: "success", message: item.enabled ? "Playlist item disabled." : "Playlist item enabled." });
    } catch (error) {
      setFeedback({ type: "error", message: error?.message || "Unable to update playlist item." });
    }
  }

  async function handleDurationChange(item, nextValue) {
    if (!selectedPlaylist || item.media?.type !== TV_MEDIA_TYPES.IMAGE) return;
    try {
      await updateTvPlaylistItem(selectedPlaylist.id, item.id, {
        imageDurationSeconds: nextValue,
      });
      setFeedback({ type: "success", message: "Image duration updated." });
    } catch (error) {
      setFeedback({ type: "error", message: error?.message || "Unable to update image duration." });
    }
  }

  async function handleRemoveItem() {
    if (!selectedPlaylist || !removeState.item) return;
    try {
      setRemoveState((current) => ({ ...current, busy: true }));
      await removeTvPlaylistItem(selectedPlaylist.id, removeState.item.id);
      setRemoveState({ open: false, item: null, busy: false });
      setFeedback({ type: "success", message: "Playlist item removed." });
    } catch (error) {
      setRemoveState((current) => ({ ...current, busy: false }));
      setFeedback({ type: "error", message: error?.message || "Unable to remove playlist item." });
    }
  }

  function openOverlayEditor(item) {
    const defaults = selectedPlaylist?.presentation?.textDefaults || {};
    setOverlayState({
      open: true,
      item,
      value: normalizePlaylistItemOverlay({
        ...defaults,
        ...item.overlay,
      }),
      error: "",
      busy: false,
    });
  }

  async function handleSaveOverlay() {
    if (!selectedPlaylist || !overlayState.item || !overlayState.value) return;
    try {
      setOverlayState((current) => ({ ...current, busy: true, error: "" }));
      const value = normalizePlaylistItemOverlay(overlayState.value);
      await updatePlaylistItemOverlay(selectedPlaylist.id, overlayState.item.id, value);
      setOverlayState({ open: false, item: null, value: null, error: "", busy: false });
      setFeedback({ type: "success", message: "Offer overlay saved." });
    } catch (error) {
      setOverlayState((current) => ({
        ...current,
        busy: false,
        error: error?.message || "Unable to save offer overlay.",
      }));
    }
  }

  const hasPlaylists = playlistRows.length > 0;

  return (
    <section className="tv-display-panel">
      <div className="tv-display-panel-head">
        <div>
          <h2 className="tv-display-panel-title">Playlists</h2>
          <p className="tv-display-panel-copy">
            Build draft signage playlists with ordering, presentation rules, compatibility checks,
            offer overlays, and browser preview before publishing.
          </p>
        </div>
        <button type="button" className="tv-playlist-button" onClick={() => setCreateOpen(true)}>
          <FaPlus />
          <span>Create Playlist</span>
        </button>
      </div>

      <div className="tv-playlist-summary-grid">
        <SummaryCard label="Playlists" value={playlistStats.total} />
        <SummaryCard label="Enabled" value={playlistStats.enabled} />
        <SummaryCard label="Disabled" value={playlistStats.disabled} />
        <SummaryCard label="Playlist Items" value={playlistStats.itemCount} />
      </div>

      <div className="tv-playlist-shell">
        <div className="tv-playlist-sidebar">
          <div className="tv-playlist-toolbar">
            <div className="tv-playlist-search">
              <FaSearch color="#64748b" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search playlists..."
              />
            </div>

            <select className="tv-playlist-select" value={sort} onChange={(event) => setSort(event.target.value)}>
              {PLAYLIST_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="tv-playlist-filter-row">
            {PLAYLIST_FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`tv-playlist-chip ${filter === option.value ? "active" : ""}`}
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {playlistError ? <div className="tv-playlist-inline-error">{playlistError}</div> : null}
          {playlistLoading ? <div className="tv-playlist-inline-info">Loading playlists...</div> : null}

          <div className="tv-playlist-list">
            {!playlistLoading && !visiblePlaylists.length ? (
              <div className="tv-playlist-empty">
                <FaLayerGroup size={26} />
                <div className="tv-playlist-empty-title">
                  {hasPlaylists ? "No playlists match the current filter." : "No playlists created yet."}
                </div>
                <div className="tv-playlist-empty-copy">
                  Create your first playlist to start arranging and presenting TV advertisements.
                </div>
              </div>
            ) : null}

            {visiblePlaylists.map((playlist) => {
              const status = getCompatibilityStatusLabel(playlist);
              return (
                <button
                  key={playlist.id}
                  type="button"
                  className={`tv-playlist-card ${selectedPlaylistId === playlist.id ? "is-active" : ""}`}
                  onClick={() => attemptSelectPlaylist(playlist.id)}
                >
                  <div className="tv-playlist-card-head">
                    <div className="tv-playlist-card-title">{playlist.name}</div>
                    <span className={`tv-playlist-pill ${playlist.enabled ? "is-enabled" : "is-disabled"}`}>
                      {playlist.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div className="tv-playlist-card-copy">{playlist.description || "No description added yet."}</div>
                  <div className="tv-playlist-card-meta">
                    <span>{playlist.itemCount} items</span>
                    <span>{prettyModeLabel(playlist.presentation.displayMode)}</span>
                    <span>{status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="tv-playlist-editor">
          {!selectedPlaylist ? (
            <div className="tv-playlist-empty is-editor">
              <FaLayerGroup size={28} />
              <div className="tv-playlist-empty-title">Choose a playlist</div>
              <div className="tv-playlist-empty-copy">
                Select an existing playlist or create a new one to configure media, presentation, and preview.
              </div>
            </div>
          ) : (
            <>
              <div className="tv-playlist-editor-head">
                <div>
                  <div className="tv-playlist-section-title">{selectedPlaylist.name}</div>
                  <div className="tv-playlist-section-copy">
                    Presentation settings are saved on the playlist document. Offer overlays are saved per playlist item.
                  </div>
                  <div className="tv-playlist-loop-copy">Playback: Continuous Loop</div>
                  <div className="tv-playlist-loop-copy">
                    Publish Status: {getPlaylistPublishLabel(selectedPlaylist)}
                    {selectedPlaylist.lastPublishedVersion ? ` · Version ${selectedPlaylist.lastPublishedVersion}` : ""}
                  </div>
                </div>
                <div className="tv-playlist-editor-actions">
                  <button
                    type="button"
                    className="tv-playlist-button-secondary"
                    onClick={() => document.getElementById("tv-display-publishing")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  >
                    <FaBroadcastTower />
                    <span>{selectedPlaylist.lastPublishedVersion ? "Publish New Version" : "Publish to TV"}</span>
                  </button>
                  <button
                    type="button"
                    className="tv-playlist-button-secondary"
                    onClick={() => setDeleteState({ open: true, busy: false, error: "" })}
                  >
                    <FaTrashAlt />
                    <span>Delete Playlist</span>
                  </button>
                </div>
              </div>

              <div className="tv-playlist-form-grid">
                <div className="tv-playlist-field">
                  <label htmlFor="tv-playlist-name">Playlist name</label>
                  <input
                    id="tv-playlist-name"
                    className="tv-playlist-input"
                    value={playlistMetaState.name}
                    maxLength={120}
                    onChange={(event) =>
                      setPlaylistMetaState((current) => ({
                        ...current,
                        name: event.target.value,
                        error: "",
                      }))
                    }
                  />
                </div>
                <div className="tv-playlist-field">
                  <label htmlFor="tv-playlist-description">Description</label>
                  <textarea
                    id="tv-playlist-description"
                    className="tv-playlist-textarea"
                    value={playlistMetaState.description}
                    maxLength={500}
                    onChange={(event) =>
                      setPlaylistMetaState((current) => ({
                        ...current,
                        description: event.target.value,
                        error: "",
                      }))
                    }
                  />
                </div>
              </div>

              <div className="tv-playlist-toggle-row">
                <label className="tv-playlist-toggle">
                  <input
                    type="checkbox"
                    checked={playlistMetaState.enabled}
                    onChange={(event) =>
                      setPlaylistMetaState((current) => ({
                        ...current,
                        enabled: event.target.checked,
                      }))
                    }
                  />
                  <span>Playlist enabled</span>
                </label>
              </div>

              {playlistMetaState.error ? <div className="tv-playlist-inline-error">{playlistMetaState.error}</div> : null}

              <div className="tv-playlist-save-row">
                <button
                  type="button"
                  className="tv-playlist-button"
                  onClick={handleSavePlaylistMeta}
                  disabled={playlistMetaState.busy}
                >
                  <FaCheckCircle />
                  <span>{playlistMetaState.busy ? "Saving..." : "Save Playlist"}</span>
                </button>
              </div>

              <div className="tv-playlist-tab-row">
                {Object.values(EDITOR_TABS).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`tv-playlist-tab ${activeTab === tab ? "is-active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === EDITOR_TABS.MEDIA ? "Media" : tab === EDITOR_TABS.PRESENTATION ? "Presentation" : "Preview"}
                  </button>
                ))}
              </div>

              {activeTab === EDITOR_TABS.MEDIA ? (
                <div className="tv-playlist-items-card">
                  <div className="tv-playlist-editor-head">
                    <div>
                      <div className="tv-playlist-section-title">Playlist Media</div>
                      <div className="tv-playlist-section-copy">
                        Add existing Media Library items, allow duplicates, and attach offer overlay content per item.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="tv-playlist-button"
                      onClick={() => setPickerOpen(true)}
                      disabled={mediaLoading || addingMedia}
                    >
                      <FaPlus />
                      <span>{addingMedia ? "Adding..." : "Add Media"}</span>
                    </button>
                  </div>

                  {compatibility.incompatibleCount ? (
                    <div className="tv-playlist-inline-error">
                      Compatibility warning: this playlist contains {compatibility.incompatibleCount} incompatible item{compatibility.incompatibleCount === 1 ? "" : "s"} for {prettyModeLabel(selectedPlaylist.presentation.displayMode)}.
                    </div>
                  ) : null}
                  {itemsError ? <div className="tv-playlist-inline-error">{itemsError}</div> : null}
                  {mediaError ? <div className="tv-playlist-inline-error">{mediaError}</div> : null}
                  {itemsLoading ? <div className="tv-playlist-inline-info">Loading playlist items...</div> : null}
                  {ordering ? <div className="tv-playlist-inline-info">Saving order...</div> : null}

                  {!itemsLoading && !decoratedItems.length ? (
                    <div className="tv-playlist-empty is-items">
                      <FaCopy size={24} />
                      <div className="tv-playlist-empty-title">This playlist is empty.</div>
                      <div className="tv-playlist-empty-copy">
                        Add images or videos from your Media Library, then configure presentation and offer overlays.
                      </div>
                    </div>
                  ) : null}

                  <div className="tv-playlist-item-list">
                    {decoratedItems.map((item, index) => (
                      <PlaylistItemRow
                        key={item.id}
                        item={item}
                        index={index}
                        count={decoratedItems.length}
                        dragging={draggingItemId === item.id}
                        incompatible={!isMediaCompatible(item.media, selectedPlaylist.presentation.displayMode)}
                        onDragStart={() => setDraggingItemId(item.id)}
                        onDragEnd={() => setDraggingItemId("")}
                        onDrop={() => {
                          if (ordering) return;
                          const fromIndex = decoratedItems.findIndex((entry) => entry.id === draggingItemId);
                          setDraggingItemId("");
                          if (fromIndex >= 0) handleMoveItem(fromIndex, index);
                        }}
                        onMoveUp={() => handleMoveItem(index, index - 1)}
                        onMoveDown={() => handleMoveItem(index, index + 1)}
                        onToggle={() => handleToggleItem(item)}
                        onDurationChange={handleDurationChange}
                        onRemove={() => setRemoveState({ open: true, item, busy: false })}
                        onEditOverlay={() => openOverlayEditor(item)}
                        ordering={ordering}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {activeTab === EDITOR_TABS.PRESENTATION ? (
                <div className="tv-playlist-items-card">
                  <div className="tv-playlist-editor-head">
                    <div>
                      <div className="tv-playlist-section-title">Presentation Settings</div>
                      <div className="tv-playlist-section-copy">
                        Control display mode, orientation, fit, transition, text defaults, and browser preview behavior.
                      </div>
                    </div>
                    {presentationDirty ? <div className="tv-playlist-dirty-pill">Unsaved changes</div> : null}
                  </div>

                  {presentationError ? <div className="tv-playlist-inline-error">{presentationError}</div> : null}

                  <div className="tv-playlist-settings-grid">
                    <FieldSelect
                      label="Display Mode"
                      value={presentationState.displayMode}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "displayMode", value)}
                      options={DISPLAY_MODE_OPTIONS}
                    />
                    <FieldSelect
                      label="Orientation"
                      value={presentationState.orientation}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "orientation", value)}
                      options={ORIENTATION_OPTIONS}
                    />
                    <FieldSelect
                      label="Media Fit"
                      value={presentationState.fitMode}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "fitMode", value)}
                      options={FIT_MODE_OPTIONS}
                    />
                    <FieldSelect
                      label="Transition"
                      value={presentationState.transition}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "transition", value)}
                      options={TRANSITION_OPTIONS}
                    />
                    <FieldNumber
                      label="Transition Duration (ms)"
                      value={presentationState.transitionDurationMs}
                      min={MIN_TRANSITION_DURATION_MS}
                      max={MAX_TRANSITION_DURATION_MS}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "transitionDurationMs", value)}
                    />
                    <FieldNumber
                      label="Default Image Duration (s)"
                      value={presentationState.defaultImageDurationSeconds}
                      min={MIN_IMAGE_DURATION_SECONDS}
                      max={MAX_IMAGE_DURATION_SECONDS}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "defaultImageDurationSeconds", value)}
                    />
                    <FieldColor
                      label="Background Color"
                      value={presentationState.backgroundColor}
                      onChange={(value) => updatePresentationState(setPresentationState, setPresentationDirty, "backgroundColor", value)}
                    />
                    <label className="tv-playlist-toggle tv-playlist-toggle-card">
                      <input
                        type="checkbox"
                        checked={presentationState.showTextOverlay}
                        onChange={(event) => updatePresentationState(setPresentationState, setPresentationDirty, "showTextOverlay", event.target.checked)}
                      />
                      <span>Allow text overlays</span>
                    </label>
                  </div>

                  <div className="tv-playlist-text-defaults-card">
                    <div className="tv-playlist-section-title">Global Text Defaults</div>
                    <div className="tv-playlist-section-copy">
                      New overlay-enabled items start from these defaults. Individual items can override them.
                    </div>

                    <div className="tv-playlist-settings-grid">
                      <FieldColor
                        label="Text Color"
                        value={presentationState.textDefaults.textColor}
                        onChange={(value) => updatePresentationTextDefaults(setPresentationState, setPresentationDirty, "textColor", value)}
                      />
                      <FieldColor
                        label="Accent Color"
                        value={presentationState.textDefaults.accentColor}
                        onChange={(value) => updatePresentationTextDefaults(setPresentationState, setPresentationDirty, "accentColor", value)}
                      />
                      <FieldColor
                        label="Overlay Background"
                        value={presentationState.textDefaults.backgroundColor}
                        onChange={(value) => updatePresentationTextDefaults(setPresentationState, setPresentationDirty, "backgroundColor", value)}
                      />
                      <FieldNumber
                        label="Overlay Opacity (%)"
                        value={Math.round(Number(presentationState.textDefaults.backgroundOpacity || DEFAULT_OVERLAY_BACKGROUND_OPACITY) * 100)}
                        min={0}
                        max={100}
                        onChange={(value) =>
                          updatePresentationTextDefaults(
                            setPresentationState,
                            setPresentationDirty,
                            "backgroundOpacity",
                            Number(value) / 100
                          )
                        }
                      />
                      <FieldSelect
                        label="Text Position"
                        value={presentationState.textDefaults.position}
                        onChange={(value) => updatePresentationTextDefaults(setPresentationState, setPresentationDirty, "position", value)}
                        options={POSITION_OPTIONS.map((value) => ({ value, label: prettyPositionLabel(value) }))}
                      />
                      <FieldSelect
                        label="Text Align"
                        value={presentationState.textDefaults.textAlign}
                        onChange={(value) => updatePresentationTextDefaults(setPresentationState, setPresentationDirty, "textAlign", value)}
                        options={TEXT_ALIGNMENT_OPTIONS}
                      />
                      <FieldSelect
                        label="Text Animation"
                        value={presentationState.textDefaults.animation}
                        onChange={(value) => updatePresentationTextDefaults(setPresentationState, setPresentationDirty, "animation", value)}
                        options={TEXT_ANIMATION_OPTIONS}
                      />
                    </div>
                  </div>

                  <CompatibilityPanel compatibility={compatibility} displayMode={presentationState.displayMode} />

                  <div className="tv-playlist-save-row">
                    <button type="button" className="tv-playlist-button" onClick={handleSavePresentation} disabled={presentationBusy}>
                      <FaPalette />
                      <span>{presentationBusy ? "Saving..." : "Save Presentation"}</span>
                    </button>
                  </div>
                </div>
              ) : null}

              {activeTab === EDITOR_TABS.PREVIEW ? (
                <div className="tv-playlist-items-card">
                  <div className="tv-playlist-editor-head">
                    <div>
                      <div className="tv-playlist-section-title">Preview Playlist</div>
                      <div className="tv-playlist-section-copy">
                        Browser-side preview only. This does not publish anything to TVs or touch `activePlaylistId`.
                      </div>
                    </div>
                    <div className="tv-playlist-preview-controls">
                      <button
                        type="button"
                        className="tv-playlist-icon-button"
                        onClick={() => setPreviewIndex((current) => previousPreviewIndex(current, compatibleItems.length))}
                        disabled={!compatibleItems.length}
                      >
                        <FaStepBackward />
                      </button>
                      <button
                        type="button"
                        className="tv-playlist-icon-button"
                        onClick={() => setPreviewPlaying((current) => !current)}
                        disabled={!compatibleItems.length}
                      >
                        {previewPlaying ? <FaPause /> : <FaPlay />}
                      </button>
                      <button
                        type="button"
                        className="tv-playlist-icon-button"
                        onClick={() => setPreviewIndex((current) => nextPreviewIndex(current, compatibleItems.length))}
                        disabled={!compatibleItems.length}
                      >
                        <FaStepForward />
                      </button>
                    </div>
                  </div>

                  {!decoratedItems.length ? (
                    <div className="tv-playlist-empty is-items">
                      <div className="tv-playlist-empty-title">Nothing to preview.</div>
                      <div className="tv-playlist-empty-copy">Add media to this playlist first.</div>
                    </div>
                  ) : !compatibleItems.length ? (
                    <div className="tv-playlist-empty is-items">
                      <div className="tv-playlist-empty-title">No compatible media for the selected mode.</div>
                      <div className="tv-playlist-empty-copy">
                        Switch display mode or review the incompatible items list in Presentation.
                      </div>
                    </div>
                  ) : (
                    <>
                      <TvPresentationPreviewCanvas
                        playlist={selectedPlaylist}
                        item={currentPreviewItem}
                        autoPlayVideo={previewPlaying}
                        muted
                      />
                      <div className="tv-playlist-preview-meta-row">
                        <span>Item {previewIndex + 1} of {compatibleItems.length}</span>
                        <span>{prettyModeLabel(selectedPlaylist.presentation.displayMode)}</span>
                        <span>{selectedPlaylist.presentation.orientation}</span>
                        <span>{selectedPlaylist.presentation.fitMode}</span>
                        <span>{selectedPlaylist.presentation.transition}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {createOpen ? (
        <CreatePlaylistModal
          state={createState}
          onClose={() => {
            setCreateOpen(false);
            setCreateState({ name: "", description: "", error: "", busy: false });
          }}
          onChange={(patch) => setCreateState((current) => ({ ...current, ...patch, error: "" }))}
          onSubmit={handleCreatePlaylist}
        />
      ) : null}

      {pickerOpen ? (
        <MediaPickerModal
          mediaRows={mediaRows}
          mediaLoading={mediaLoading}
          displayMode={selectedPlaylist?.presentation?.displayMode || TV_DISPLAY_MODES.MIXED_PLAYLIST}
          onClose={() => setPickerOpen(false)}
          onAdd={handleAddMedia}
          addingMedia={addingMedia}
        />
      ) : null}

      {overlayState.open ? (
        <OverlayEditorModal
          state={overlayState}
          presentation={selectedPlaylist?.presentation}
          media={overlayState.item?.media || null}
          onClose={() => setOverlayState({ open: false, item: null, value: null, error: "", busy: false })}
          onChange={(patch) =>
            setOverlayState((current) => ({
              ...current,
              value: normalizePlaylistItemOverlay({
                ...current.value,
                ...patch,
              }),
              error: "",
            }))
          }
          onSubmit={handleSaveOverlay}
        />
      ) : null}

      <ConfirmModal
        open={deleteState.open}
        title="Delete this playlist?"
        message="The playlist and its item order will be permanently removed. Original images and videos will remain in the Media Library."
        confirmText="Delete Playlist"
        cancelText="Cancel"
        loading={deleteState.busy}
        error={deleteState.error}
        onCancel={() => setDeleteState({ open: false, busy: false, error: "" })}
        onConfirm={handleDeletePlaylist}
      />

      <ConfirmModal
        open={removeState.open}
        title="Remove this item from the playlist?"
        message="The original media will remain in the Media Library and Firebase Storage."
        confirmText="Remove Item"
        cancelText="Cancel"
        loading={removeState.busy}
        onCancel={() => setRemoveState({ open: false, item: null, busy: false })}
        onConfirm={handleRemoveItem}
      />

      {feedback ? (
        <div className={`tv-playlist-toast ${feedback.type === "success" ? "is-success" : "is-error"}`}>
          {feedback.message}
        </div>
      ) : null}
    </section>
  );
}

function CreatePlaylistModal({ state, onClose, onChange, onSubmit }) {
  return (
    <div className="tv-playlist-modal-overlay" onMouseDown={onClose}>
      <div className="tv-playlist-modal is-small" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tv-playlist-modal-head">
          <div>
            <h3 className="tv-playlist-modal-title">Create Playlist</h3>
            <p className="tv-playlist-modal-copy">Create the draft playlist first, then configure its presentation.</p>
          </div>
          <button type="button" className="tv-playlist-icon-button" onClick={onClose} aria-label="Close create playlist">
            <FaWindowClose />
          </button>
        </div>
        <div className="tv-playlist-field">
          <label htmlFor="tv-playlist-create-name">Playlist name</label>
          <input
            id="tv-playlist-create-name"
            className="tv-playlist-input"
            value={state.name}
            maxLength={120}
            autoFocus
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </div>
        <div className="tv-playlist-field">
          <label htmlFor="tv-playlist-create-description">Description</label>
          <textarea
            id="tv-playlist-create-description"
            className="tv-playlist-textarea"
            value={state.description}
            maxLength={500}
            onChange={(event) => onChange({ description: event.target.value })}
          />
        </div>
        {state.error ? <div className="tv-playlist-inline-error">{state.error}</div> : null}
        <div className="tv-playlist-modal-actions">
          <button type="button" className="tv-playlist-button-secondary" onClick={onClose} disabled={state.busy}>
            Cancel
          </button>
          <button type="button" className="tv-playlist-button" onClick={onSubmit} disabled={state.busy}>
            <FaPlus />
            <span>{state.busy ? "Creating..." : "Create Playlist"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function MediaPickerModal({ mediaRows, mediaLoading, displayMode, onClose, onAdd, addingMedia }) {
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filter, setFilter] = useState(getDefaultPickerFilter(displayMode));
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    setFilter(getDefaultPickerFilter(displayMode));
    setSelectedIds([]);
  }, [displayMode]);

  const visibleMedia = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    let rows = [...mediaRows];
    if (query) rows = rows.filter((row) => row.searchText.includes(query));
    if (filter === TV_MEDIA_FILTERS.VIDEO) rows = rows.filter((row) => row.type === TV_MEDIA_TYPES.VIDEO);
    if (filter === TV_MEDIA_FILTERS.IMAGE) rows = rows.filter((row) => row.type === TV_MEDIA_TYPES.IMAGE);
    if (filter === TV_MEDIA_FILTERS.ACTIVE) rows = rows.filter((row) => row.enabled);
    if (filter === TV_MEDIA_FILTERS.DISABLED) rows = rows.filter((row) => !row.enabled);
    rows.sort((left, right) => right.createdAtMs - left.createdAtMs);
    return rows;
  }, [deferredSearch, filter, mediaRows]);

  const selectableVisibleIds = useMemo(
    () =>
      visibleMedia
        .filter((media) => media.enabled && isMediaCompatible(media, displayMode))
        .map((media) => media.id),
    [displayMode, visibleMedia]
  );

  function toggleSelection(media) {
    if (!media.enabled || !isMediaCompatible(media, displayMode)) return;
    setSelectedIds((current) =>
      current.includes(media.id) ? current.filter((id) => id !== media.id) : [...current, media.id]
    );
  }

  return (
    <div className="tv-playlist-modal-overlay" onMouseDown={onClose}>
      <div className="tv-playlist-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tv-playlist-modal-head">
          <div>
            <h3 className="tv-playlist-modal-title">Add Media to Playlist</h3>
            <p className="tv-playlist-modal-copy">
              Filtered selection follows the current display mode. Incompatible or disabled media stays visible for context.
            </p>
          </div>
          <button type="button" className="tv-playlist-icon-button" onClick={onClose} aria-label="Close media picker">
            <FaWindowClose />
          </button>
        </div>

        <div className="tv-playlist-toolbar">
          <div className="tv-playlist-search">
            <FaSearch color="#64748b" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search media..." />
          </div>
          <select className="tv-playlist-select" value={filter} onChange={(event) => setFilter(event.target.value)}>
            <option value={TV_MEDIA_FILTERS.ALL}>All Media</option>
            <option value={TV_MEDIA_FILTERS.VIDEO}>Videos</option>
            <option value={TV_MEDIA_FILTERS.IMAGE}>Images</option>
            <option value={TV_MEDIA_FILTERS.ACTIVE}>Enabled</option>
            <option value={TV_MEDIA_FILTERS.DISABLED}>Disabled</option>
          </select>
        </div>

        <div className="tv-playlist-picker-actions">
          <button type="button" className="tv-playlist-button-secondary" onClick={() => setSelectedIds(selectableVisibleIds)}>
            Select All Filtered
          </button>
          <button type="button" className="tv-playlist-button-secondary" onClick={() => setSelectedIds([])}>
            Clear Selection
          </button>
          <div className="tv-playlist-picker-count">{selectedIds.length} selected</div>
        </div>

        {mediaLoading ? <div className="tv-playlist-inline-info">Loading media library...</div> : null}

        <div className="tv-playlist-picker-list">
          {!mediaLoading && !visibleMedia.length ? (
            <div className="tv-playlist-empty is-picker">
              <div className="tv-playlist-empty-title">No media available.</div>
              <div className="tv-playlist-empty-copy">Upload media first, then return here to build playlists.</div>
            </div>
          ) : null}

          {visibleMedia.map((media) => {
            const checked = selectedIds.includes(media.id);
            const incompatible = !isMediaCompatible(media, displayMode);
            return (
              <label
                key={media.id}
                className={`tv-playlist-picker-item ${checked ? "is-selected" : ""} ${media.enabled ? "" : "is-disabled"} ${incompatible ? "is-incompatible" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSelection(media)}
                  disabled={!media.enabled || incompatible}
                />
                <div className="tv-playlist-picker-main">
                  <div className="tv-playlist-picker-name">{media.name || media.originalName || "Untitled media"}</div>
                  <div className="tv-playlist-picker-meta">
                    <span>{media.type}</span>
                    <span>{formatMediaRuntime(media)}</span>
                    <span>{formatFileSize(media.sizeBytes)}</span>
                    <span>{media.enabled ? "Enabled" : "Disabled"}</span>
                    <span>{incompatible ? `Not compatible with ${prettyModeLabel(displayMode)}` : "Compatible"}</span>
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="tv-playlist-modal-actions">
          <button type="button" className="tv-playlist-button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="tv-playlist-button" onClick={() => onAdd(selectedIds)} disabled={!selectedIds.length || addingMedia}>
            <FaPlus />
            <span>{addingMedia ? "Adding..." : `Add ${selectedIds.length || ""} Item${selectedIds.length === 1 ? "" : "s"}`}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function OverlayEditorModal({ state, presentation, media, onClose, onChange, onSubmit }) {
  const overlay = state.value || getDefaultPlaylistItemOverlay();
  const previewPlaylist = {
    presentation: normalizePlaylistPresentation(presentation),
  };
  const previewItem = {
    media,
    imageDurationSeconds: DEFAULT_IMAGE_DURATION_SECONDS,
    overlay,
  };

  return (
    <div className="tv-playlist-modal-overlay" onMouseDown={onClose}>
      <div className="tv-playlist-modal tv-playlist-overlay-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tv-playlist-modal-head">
          <div>
            <h3 className="tv-playlist-modal-title">Offer / Text Overlay</h3>
            <p className="tv-playlist-modal-copy">Overlay content is saved on this playlist item only, not on the shared Media Library record.</p>
          </div>
          <button type="button" className="tv-playlist-icon-button" onClick={onClose} aria-label="Close overlay editor">
            <FaWindowClose />
          </button>
        </div>

        <div className="tv-playlist-overlay-shell">
          <div className="tv-playlist-overlay-fields">
            <label className="tv-playlist-toggle">
              <input
                type="checkbox"
                checked={overlay.enabled}
                onChange={(event) => onChange({ enabled: event.target.checked })}
              />
              <span>Show Offer/Text</span>
            </label>

            <TextField label="Badge" value={overlay.badge} maxLength={OFFER_TEXT_LIMITS.badge} onChange={(value) => onChange({ badge: value })} />
            <TextField label="Headline" value={overlay.headline} maxLength={OFFER_TEXT_LIMITS.headline} onChange={(value) => onChange({ headline: value })} />
            <TextField label="Subheadline" value={overlay.subheadline} maxLength={OFFER_TEXT_LIMITS.subheadline} onChange={(value) => onChange({ subheadline: value })} />
            <TextField label="Price" value={overlay.priceText} maxLength={OFFER_TEXT_LIMITS.priceText} onChange={(value) => onChange({ priceText: value })} />
            <TextField label="CTA" value={overlay.ctaText} maxLength={OFFER_TEXT_LIMITS.ctaText} onChange={(value) => onChange({ ctaText: value })} />

            <FieldSelect label="Text Position" value={overlay.position} onChange={(value) => onChange({ position: value })} options={POSITION_OPTIONS.map((value) => ({ value, label: prettyPositionLabel(value) }))} />
            <FieldSelect label="Text Align" value={overlay.textAlign} onChange={(value) => onChange({ textAlign: value })} options={TEXT_ALIGNMENT_OPTIONS} />
            <FieldSelect label="Text Animation" value={overlay.animation} onChange={(value) => onChange({ animation: value })} options={TEXT_ANIMATION_OPTIONS} />
            <FieldColor label="Text Color" value={overlay.textColor} onChange={(value) => onChange({ textColor: value })} />
            <FieldColor label="Accent Color" value={overlay.accentColor} onChange={(value) => onChange({ accentColor: value })} />
            <FieldColor label="Overlay Background" value={overlay.backgroundColor} onChange={(value) => onChange({ backgroundColor: value })} />
            <FieldNumber label="Overlay Opacity (%)" value={Math.round((overlay.backgroundOpacity ?? DEFAULT_OVERLAY_BACKGROUND_OPACITY) * 100)} min={0} max={100} onChange={(value) => onChange({ backgroundOpacity: Number(value) / 100 })} />
          </div>

          <div className="tv-playlist-overlay-preview">
            <div className="tv-playlist-section-title">Live Preview</div>
            <TvPresentationPreviewCanvas playlist={previewPlaylist} item={previewItem} muted />
          </div>
        </div>

        {state.error ? <div className="tv-playlist-inline-error">{state.error}</div> : null}

        <div className="tv-playlist-modal-actions">
          <button type="button" className="tv-playlist-button-secondary" onClick={onClose} disabled={state.busy}>
            Cancel
          </button>
          <button type="button" className="tv-playlist-button" onClick={onSubmit} disabled={state.busy}>
            <FaCheckCircle />
            <span>{state.busy ? "Saving..." : "Save Offer"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function PlaylistItemRow({
  item,
  index,
  count,
  dragging,
  incompatible,
  onDragStart,
  onDragEnd,
  onDrop,
  onMoveUp,
  onMoveDown,
  onToggle,
  onDurationChange,
  onRemove,
  onEditOverlay,
  ordering,
}) {
  const [duration, setDuration] = useState(item.imageDurationSeconds || DEFAULT_IMAGE_DURATION_SECONDS);

  useEffect(() => {
    setDuration(item.imageDurationSeconds || DEFAULT_IMAGE_DURATION_SECONDS);
  }, [item.id, item.imageDurationSeconds]);

  const mediaMissing = !item.media;
  const mediaDisabled = item.media && !item.media.enabled;
  const isImage = item.media?.type === TV_MEDIA_TYPES.IMAGE;
  const overlayEnabled = item.overlay?.enabled;

  return (
    <div
      className={`tv-playlist-item ${dragging ? "is-dragging" : ""} ${incompatible ? "is-incompatible" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
    >
      <div className="tv-playlist-item-order">
        <FaGripLines />
        <span>{index + 1}</span>
      </div>

      <div className="tv-playlist-item-body">
        <div className="tv-playlist-item-title">{item.media?.name || "Media unavailable"}</div>
        <div className="tv-playlist-item-meta">
          <span>{item.media?.type || "Missing"}</span>
          <span>{item.enabled ? "Enabled" : "Disabled"}</span>
          <span>{formatMediaRuntime(item.media)}</span>
          <span>{item.media ? formatMediaDimensions(item.media) : "Unknown dimensions"}</span>
          <span>{item.media ? formatFileSize(item.media.sizeBytes) : item.mediaId}</span>
        </div>

        {mediaMissing ? <div className="tv-playlist-warning">Media unavailable. The original Media Library item was deleted.</div> : null}
        {mediaDisabled ? <div className="tv-playlist-warning">Media disabled. It stays in the playlist until you remove it.</div> : null}
        {incompatible ? <div className="tv-playlist-warning">This item is incompatible with the current display mode and will not be auto-deleted.</div> : null}

        {isImage ? (
          <div className="tv-playlist-duration-row">
            <label htmlFor={`playlist-duration-${item.id}`}>Image duration (seconds)</label>
            <input
              id={`playlist-duration-${item.id}`}
              type="number"
              min={MIN_IMAGE_DURATION_SECONDS}
              max={MAX_IMAGE_DURATION_SECONDS}
              className="tv-playlist-duration-input"
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
              onBlur={() => onDurationChange(item, duration)}
            />
          </div>
        ) : null}

        <div className="tv-playlist-item-overlay-row">
          <span className={`tv-playlist-pill ${overlayEnabled ? "is-enabled" : "is-disabled"}`}>
            {overlayEnabled ? "Offer Visible" : "Offer Hidden"}
          </span>
          <button type="button" className="tv-playlist-button-secondary" onClick={onEditOverlay}>
            <FaPalette />
            <span>Text Overlay</span>
          </button>
        </div>
      </div>

      <div className="tv-playlist-item-actions">
        <button type="button" className="tv-playlist-icon-button" onClick={onMoveUp} disabled={index === 0 || ordering}>
          <FaArrowUp />
        </button>
        <button type="button" className="tv-playlist-icon-button" onClick={onMoveDown} disabled={index === count - 1 || ordering}>
          <FaArrowDown />
        </button>
        <button type="button" className="tv-playlist-icon-button" onClick={onToggle} disabled={ordering}>
          {item.enabled ? <FaPowerOff /> : <FaCheckCircle />}
        </button>
        <button type="button" className="tv-playlist-icon-button is-danger" onClick={onRemove} disabled={ordering}>
          <FaTrashAlt />
        </button>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="tv-playlist-summary-card">
      <div className="tv-playlist-summary-label">{label}</div>
      <div className="tv-playlist-summary-value">{value}</div>
    </div>
  );
}

function CompatibilityPanel({ compatibility, displayMode }) {
  return (
    <div className="tv-playlist-compatibility-card">
      <div className="tv-playlist-section-title">Compatibility Validation</div>
      <div className="tv-playlist-section-copy">
        Display Mode: {prettyModeLabel(displayMode)}. No incompatible items are automatically deleted.
      </div>
      <div className="tv-playlist-compatibility-grid">
        <div className="tv-playlist-compatibility-item">
          <strong>Status</strong>
          <span>{compatibility.status}</span>
        </div>
        <div className="tv-playlist-compatibility-item">
          <strong>Compatible</strong>
          <span>{compatibility.compatibleCount}</span>
        </div>
        <div className="tv-playlist-compatibility-item">
          <strong>Incompatible</strong>
          <span>{compatibility.incompatibleCount}</span>
        </div>
      </div>
      {compatibility.incompatibleItems.length ? (
        <div className="tv-playlist-warning-list">
          {compatibility.incompatibleItems.map((item) => (
            <div key={item.id} className="tv-playlist-warning-line">
              <FaExclamationTriangle />
              <span>{item.media?.name || "Media unavailable"} is not compatible with {prettyModeLabel(displayMode)}.</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TextField({ label, value, maxLength, onChange }) {
  return (
    <div className="tv-playlist-field">
      <label>{label}</label>
      <input className="tv-playlist-input" value={value} maxLength={maxLength} onChange={(event) => onChange(event.target.value)} />
      <div className="tv-playlist-char-count">{String(value || "").length}/{maxLength}</div>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <div className="tv-playlist-field">
      <label>{label}</label>
      <select className="tv-playlist-select tv-playlist-select-full" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

function FieldNumber({ label, value, min, max, onChange }) {
  return (
    <div className="tv-playlist-field">
      <label>{label}</label>
      <input className="tv-playlist-input" type="number" min={min} max={max} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function FieldColor({ label, value, onChange }) {
  return (
    <div className="tv-playlist-field">
      <label>{label}</label>
      <div className="tv-playlist-color-field">
        <input className="tv-playlist-color-input" type="color" value={value} onChange={(event) => onChange(event.target.value.toUpperCase())} />
        <input className="tv-playlist-input" value={value} maxLength={7} onChange={(event) => onChange(event.target.value.toUpperCase())} />
      </div>
    </div>
  );
}

function getPresentationState() {
  return normalizePlaylistPresentation({});
}

function updatePresentationState(setState, setDirty, key, value) {
  setState((current) => ({ ...current, [key]: value }));
  setDirty(true);
}

function updatePresentationTextDefaults(setState, setDirty, key, value) {
  setState((current) => ({
    ...current,
    textDefaults: {
      ...current.textDefaults,
      [key]: value,
    },
  }));
  setDirty(true);
}

function getDefaultPickerFilter(displayMode) {
  if (displayMode === TV_DISPLAY_MODES.VIDEO_ONLY || displayMode === TV_DISPLAY_MODES.VIDEO_WITH_TEXT) {
    return TV_MEDIA_FILTERS.VIDEO;
  }
  if (displayMode === TV_DISPLAY_MODES.IMAGE_SLIDER || displayMode === TV_DISPLAY_MODES.IMAGE_WITH_TEXT) {
    return TV_MEDIA_FILTERS.IMAGE;
  }
  return TV_MEDIA_FILTERS.ALL;
}

function isMediaCompatible(media, displayMode) {
  if (!media) return false;
  if (displayMode === TV_DISPLAY_MODES.VIDEO_ONLY || displayMode === TV_DISPLAY_MODES.VIDEO_WITH_TEXT) {
    return media.type === TV_MEDIA_TYPES.VIDEO;
  }
  if (displayMode === TV_DISPLAY_MODES.IMAGE_SLIDER || displayMode === TV_DISPLAY_MODES.IMAGE_WITH_TEXT) {
    return media.type === TV_MEDIA_TYPES.IMAGE;
  }
  return media.type === TV_MEDIA_TYPES.VIDEO || media.type === TV_MEDIA_TYPES.IMAGE;
}

function getCompatibilitySummary(displayMode, items) {
  const incompatibleItems = items.filter((item) => !isMediaCompatible(item.media, displayMode));
  const compatibleCount = items.length - incompatibleItems.length;
  return {
    status: incompatibleItems.length ? "WARNING" : "READY",
    items,
    compatibleCount,
    incompatibleCount: incompatibleItems.length,
    incompatibleItems,
  };
}

function getCompatibilityStatusLabel(playlist) {
  return playlist.itemCount ? "Draft" : "Empty";
}

function getPlaylistPublishLabel(playlist) {
  if (playlist?.hasUnpublishedChanges && playlist?.lastPublishedVersion) return "CHANGES NOT PUBLISHED";
  if (playlist?.lastPublishedVersion) return "PUBLISHED";
  return "DRAFT";
}

function sortPlaylists(left, right, sort) {
  if (sort === PLAYLIST_SORT.OLDEST) return left.createdAtMs - right.createdAtMs;
  if (sort === PLAYLIST_SORT.NAME_ASC) return String(left.name || "").localeCompare(String(right.name || ""));
  if (sort === PLAYLIST_SORT.NAME_DESC) return String(right.name || "").localeCompare(String(left.name || ""));
  return right.createdAtMs - left.createdAtMs;
}

function resolvePlaylistError(error, fallback) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("permission-denied")) return "You do not have permission to manage TV playlists.";
  if (code.includes("unavailable")) return "Firestore is temporarily unavailable. Please try again.";
  return fallback;
}

function movePlaylistItems(items = [], fromIndex, toIndex) {
  const list = [...items];
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return list;
  const [moved] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, moved);
  return list.map((item, index) => ({ ...item, order: index }));
}

function nextPreviewIndex(current, total) {
  if (!total) return 0;
  return (current + 1) % total;
}

function previousPreviewIndex(current, total) {
  if (!total) return 0;
  return (current - 1 + total) % total;
}

function formatMediaRuntime(media) {
  if (!media) return "Media unavailable";
  if (media.type === TV_MEDIA_TYPES.IMAGE) return "Image";
  const value = Number(media.durationSeconds);
  if (!Number.isFinite(value) || value <= 0) return "Video duration unavailable";
  const total = Math.round(value);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatMediaDimensions(media) {
  if (!media?.width || !media?.height) return "Unknown dimensions";
  return `${media.width} x ${media.height}`;
}

function prettyModeLabel(mode) {
  const option = DISPLAY_MODE_OPTIONS.find((entry) => entry.value === mode);
  return option?.label || mode;
}

function prettyPositionLabel(value) {
  return value.replaceAll("_", " ");
}
